import axios from 'axios';

const BASE_API_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';
const API_URL = `${BASE_API_URL}/api/v1`;

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: { 'Content-Type': 'application/json' },
});

let isRefreshing = false;
let failedQueue = [];
let isRedirecting = false;

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    error ? prom.reject(error) : prom.resolve(token);
  });
  failedQueue = [];
};

const publicPaths = [
  '/auth/register',
  '/auth/login',
  '/auth/token/refresh',
  '/token/refresh',
  '/auth/password-reset/request',
  '/auth/password-reset/confirm',
  '/products/categories',
  '/products/main-categories',
  '/products/latest',
  '/products/list',
  '/products/search',
  '/products/tags',
  '/books',
  '/profile/daily-message',
  '/health',
];

// --- Interceptor درخواست ---
apiClient.interceptors.request.use((config) => {
  const urlPath = (config.url || '').replace(/^\//, '');
  const isPublic = publicPaths.some(path => {
    const cleanPath = path.replace(/^\//, '');
    return urlPath === cleanPath || urlPath.startsWith(cleanPath + '/');
  });

  if (!isPublic) {
    const accessToken = localStorage.getItem('access_token');
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }
  }

  return config;
}, error => Promise.reject(error));

// --- Interceptor پاسخ ---
apiClient.interceptors.response.use(
  response => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (originalRequest.url.includes('/token/refresh/')) {
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        if (!isRedirecting && !window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/register')) {
          isRedirecting = true;
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }

      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        const refreshToken = localStorage.getItem('refresh_token');
        if (!refreshToken) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          if (!isRedirecting && !window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/register')) {
            isRedirecting = true;
            window.location.href = '/login';
          }
          isRefreshing = false;
          return Promise.reject(error);
        }

        try {
          let res;
          try {
            res = await axios.post(`${API_URL}/auth/token/refresh/`, { refresh: refreshToken });
          } catch {
            res = await axios.post(`${API_URL}/token/refresh/`, { refresh: refreshToken });
          }
          const newAccess = res.data.access;
          localStorage.setItem('access_token', newAccess);
          if (res.data.refresh) {
            localStorage.setItem('refresh_token', res.data.refresh);
          }

          originalRequest.headers['Authorization'] = `Bearer ${newAccess}`;
          processQueue(null, newAccess);
          isRefreshing = false;
          return apiClient(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          processQueue(refreshError, null);
          isRefreshing = false;
          if (!isRedirecting && !window.location.pathname.startsWith('/login') && !window.location.pathname.startsWith('/register')) {
            isRedirecting = true;
            window.location.href = '/login';
          }
          return Promise.reject(refreshError);
        }
      }

      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(token => {
        originalRequest.headers['Authorization'] = `Bearer ${token}`;
        return apiClient(originalRequest);
      }).catch(err => Promise.reject(err));
    }

    return Promise.reject(error);
  }
);

export default apiClient;